import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect } from "react";
import { defaultWMS2040_R01Filter, WMS2040_R01Filter, useWMS2040_R01Store } from "src/containers/mes/WMS/WMS2040_R01/Store_WMS2040_R01";
import { WMS2040_R01HeaderDto, WMS2040_R01DetailDto } from "./Dto_WMS2040_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useToast } from "src/hooks/useToast";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { showWarning, ShowSuccessFetch, showError, showSuccess } from "src/utils/toast/toastUtil";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler} from "src/utils/grid/GridHandlerContent";
import {
	downloadDetailExcel_WMS2040_R01,
	getWMS2040_R01AllCount,
	getWMS2040_R01List,
	getDetailWMS2040_R01List,
} from "./Api_WMS2040_R01";

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS2040_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<WMS2040_R01DetailDto> | null>;
	setMessage: (message: string) => void;
}

/**
 * 재고 현황 관리 Content 컴포넌트
 * 액션바(조회/초기화/엑셀)와 주요 로직 담당
 */
export const Content_WMS2040_R01 = (props: Props) => {

	const { gridRef, detailGridRef } = props;
	const { setMessage } = useUiuxStore();
	const {
		WMS2040_R01Filter,
		WMS2040_R01PastFilter, setWMS2040_R01PastFilter,
		WMS2040_R01List, setWMS2040_R01List,
		setOriginalWMS2040_R01DetailList,
		selectedWMS2040_R01, setSelectedWMS2040_R01,
		selectedRowInfo, setSelectedRowInfo,
		setWMS2040_R01DetailList,
		initSelectedWMS2040_R01DetailList,
		allCount, setAllCount,
		initData,
		detailFetchTrigger, setDetailFetchTrigger,
		setScrollTop,
		setDetailScrollTop,
	} = useWMS2040_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { success, error: showError } = useToast();

	// 권한 정보 로드 (useFetchActionbarAuth 훅 사용)
	useFetchActionbarAuth("WMS2040_R01");

	// 로컬 타임존 기준 YYYY-MM-DD 변환 (UTC로 변환하지 않음)
	const formatDateLocal = (date: Date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};
	const getParams = (filter: WMS2040_R01Filter) => {
		const startDate = filter.txDt?.from ? formatDateLocal(new Date(filter.txDt.from)) : (filter.startDate || '');
		const endDate = filter.txDt?.to ? formatDateLocal(new Date(filter.txDt.to)) : (filter.endDate || '');
		return {
			compCd: filter.compCd || '',
			logicCenter: filter.logicCenter || '',
			warehouseCd: filter.warehouseCd || '',
			brandCd: filter.brandCd || '',
			partnerCd: filter.partnerCd || '',
			itemCd: filter.itemCd || '',
			itemGroup: filter.itemGroup || '',
			startDate,
			endDate,
			searchText: filter.searchText || '',
			includeZeroStock: filter.includeZeroStock ?? false,
		};
	};

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getWMS2040_R01AllCount(getParams(WMS2040_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [WMS2040_R01Filter, setAllCount]);

	// 상세 리스트 공용 호출 함수 (행 클릭/초기 자동 호출에서 재사용)
	const fetchDetailList = useCallback(async (logicCenter: string, warehouseCd: string, itemCd: string, itemStatus: string) => {
		// 메인 행 변경 시 상세 선택 초기화
		initSelectedWMS2040_R01DetailList();
		const responseData = (
			await getDetailWMS2040_R01List(
				{ ...getParams(WMS2040_R01Filter), logicCenter, warehouseCd, itemCd, itemStatus },
				0,
			)
		).data.data;
		if(responseData && responseData.length > 0){
			setWMS2040_R01DetailList(responseData.map((value, idx) => ({
				...value,
				valueIndex: value.valueIndex ?? idx + 1,
				orderIndex: value.orderIndex ?? idx + 1,
				orderIndexSeq: value.orderIndexSeq ?? 0,
			})));
			setOriginalWMS2040_R01DetailList(responseData);
			const selectedRowForMsg = WMS2040_R01List.find(v => v.itemCd === itemCd);
			setMessage(`${selectedRowForMsg?.itemNm || ''}의 상세내역 ${responseData.length}건을 조회하였습니다.`);
			setTimeout(() => setMessage(""), 5000);
		}else{
			showWarning('창고코드가 일치하지 않습니다.');
			setWMS2040_R01DetailList([]);
		}
	}, [WMS2040_R01Filter, setWMS2040_R01DetailList, setOriginalWMS2040_R01DetailList, detailGridRef, setMessage, WMS2040_R01List, initSelectedWMS2040_R01DetailList]);

	
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);
	
		const responseData = (await getWMS2040_R01List(
			'',
			getParams(WMS2040_R01Filter),
			WMS2040_R01List.length ?? 0
		));


		const newList = [...responseData.data.data];
		setWMS2040_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler<WMS2040_R01HeaderDto>(
				gridRef,
				(row) => {
					setSelectedWMS2040_R01(row?.itemCd);
					setSelectedRowInfo(row ? {
						itemCd: row.itemCd,
						warehouseCd: row.warehouseCd ?? '',
						logicCenter: row.logicCenter ?? '',
						itemStatus: row.itemStatus ?? '',
						valueIndex: Number(row.valueIndex),
					} : undefined);
				},
				() => true,// () => !data.find(value => value.itemCd === selectedWMS2040_R01?.itemCd)
			);
			setWMS2040_R01PastFilter(WMS2040_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		}else {
			showWarning('데이터가 없습니다.');
			setSelectedWMS2040_R01(undefined);
			setSelectedRowInfo(undefined);
			setWMS2040_R01List([]);
			setWMS2040_R01DetailList([]); 
			setWMS2040_R01DetailList([]);
			setOriginalWMS2040_R01DetailList([]);
		}
		setLoadingSpinner(false);
		return responseData;
	}, [WMS2040_R01Filter, WMS2040_R01List, setWMS2040_R01List, setWMS2040_R01PastFilter, setSelectedWMS2040_R01, setSelectedRowInfo, selectedWMS2040_R01, renewalAllCount, gridRef, setMessage, setScrollTop, setDetailScrollTop]);

	useEffect(() => {
		if (!detailFetchTrigger || !selectedWMS2040_R01 || !selectedRowInfo) return;

		const logicCenter = selectedRowInfo.logicCenter || WMS2040_R01Filter.logicCenter || '';
		const warehouseCd = selectedRowInfo.warehouseCd || WMS2040_R01Filter.warehouseCd || '';
		const status = selectedRowInfo.itemStatus || '';
		fetchDetailList(logicCenter, warehouseCd, selectedWMS2040_R01, status);
		setDetailFetchTrigger(false);
	}, [detailFetchTrigger, selectedWMS2040_R01, selectedRowInfo, fetchDetailList, setDetailFetchTrigger, WMS2040_R01Filter]);

	// 품목 선택 핸들러 (Table에서 이미 상태를 업데이트하므로 여기서는 빈 핸들러)
	const handleTableRowClick = useCallback(async (rowData: any) => {
		// Table_WMS2040_R01의 handleRowClick에서 이미 setSelectedWMS2040_R01과 setSelectedRowInfo를 호출
		// useEffect가 selectedRowInfo 변경을 감지하여 자동으로 fetchDetailList 호출
	}, []);

	// 초기화 핸들러
	const initContainerData = useCallback(() => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	// 수불 이력 엑셀 다운로드 핸들러
	const handleDetailExcelClick = useCallback(async () => {
		if (!selectedWMS2040_R01 || !selectedRowInfo) {
			showWarning('상세 조회할 품목을 선택해주세요.');
			return;
		}

		setLoadingSpinner(true);

		const logicCenter = selectedRowInfo.logicCenter || WMS2040_R01Filter.logicCenter || '';
		const warehouseCd = selectedRowInfo.warehouseCd || WMS2040_R01Filter.warehouseCd || '';
		const status = selectedRowInfo.itemStatus || '';

		const params = {
			...getParams(WMS2040_R01Filter),
			logicCenter,
			warehouseCd,
			itemCd: selectedWMS2040_R01,
			itemStatus: status
		};

		const response = await downloadDetailExcel_WMS2040_R01(
			'search',
			params,
			0
		);

		downloadSettingExcel({ responseData: response.data.data as any, fileName: '수불이력.xlsx' });		
		setLoadingSpinner(false);
	}, [selectedWMS2040_R01, selectedRowInfo, WMS2040_R01Filter, setLoadingSpinner, showWarning]);

	return {
		fetchData,
		initContainerData,
		handleDetailExcelClick,
		handleTableRowClick,
	};
};
